// noinspection JSIgnoredPromiseFromCall

import { Component, Prop, Watch, Vue } from "vue-property-decorator";

import { ValidationObserver, ValidationProvider } from "vee-validate";
import "@/plugins/vee-validate";

import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import { optimizePhotos, uploadImage } from "@/utils/upload-image";
import { fileSizeToMB, validFileSize10MB } from "@/utils/fileSizeToMB";
import { EventLocationsMixin } from "@/mixins/EventLocations.ts";

import ConfirmDlg from "@/components/popup/confirm-dialog/ConfirmDlg.vue";
import TimePicker from "@/components/pickers/TimePicker.vue";

import { CreateEvent } from "@/models/calendar/CreateEvent";

import { RecurringEventsType } from "@/enums/RecurringEventsType";
import { Calendar } from "@/models/calendar/Calendar";
import { FacilitySettingsModel } from "@/models/FacilitySettings";
import { EventsTypes } from "@/enums/EventsTypes";
import GalleryPhotosUploadDlg from "@/views/staff/gallery/GalleryPhotosUploadDlg/GalleryPhotosUploadDlg.vue";
import {GalleryImage} from "@/models/GalleryImage";
import * as firebase from "firebase";
import {waitResizedImage} from "@/utils/wait-resized-image";
import { hasAccess } from "@/utils/accessControl/accessControl";
import { AccessMatrixKeys } from "@/utils/accessControl/accessMatrixKeysEnum";

@Component({
  components: {
    ValidationProvider,
    ValidationObserver,
    ConfirmDlg,
    TimePicker,
    GalleryPhotosUploadDlg
  },
  mixins: [EventLocationsMixin]
})
export default class ManipulateCalendarEventDialog extends Vue {
  @Prop() isEdit;
  @Prop() request;
  @Prop() eventType;
  @Prop() calendarData: Calendar = new Calendar();
  @Prop() resident;

  @Watch("dialog")
  onChangeDialog(value) {
    if (value) {
      this.eventData.name = this.request.serviceName;
      this.eventData.description = this.request.description;
    }
  }

  @Watch("selectedWingsIds")
  onSelectedWingsIds(newValue) {
    this.eventData.wingsIds = newValue;
  }

  eventData: CreateEvent = new CreateEvent();

  dialog = false;
  loading = false;
  startDateMenu = false;
  endDateMenu = false;

  recurringType = RecurringEventsType;
  noEndDate = false;

  $refs!: {
    form: HTMLFormElement;
    confirmationModal: HTMLFormElement;
    GalleryPhotosUploadDlg: HTMLFormElement;
  };

  mounted() {
    if(this.eventData?.wingsIds.length) {
      (this as any).selectedWingsIds = this.eventData.wingsIds;
    }
  }

  get currentFacility(): string {
    return this.$store.state.authModule.currentFacility;
  }

  get startDate() {
    return this.eventData && this.eventData.startDate
      ? moment(this.eventData.startDate).format('ddd, '+ this.facility.dateFormat)
      : moment(new Date()).format('ddd, '+ this.facility.dateFormat);
  }

  get endDate() {
    return this.eventData && this.eventData.endDate
      ? moment(this.eventData.endDate).format('ddd, '+ this.facility.dateFormat)
      : moment(new Date()).format('ddd, '+ this.facility.dateFormat);
  }

  get facility(): FacilitySettingsModel {
    return this.$store.state.facilityModule.selectedFacility;
  }

  get user() {
    return this.$store.state.authModule.user;
  }

  get canAddEventLocation(){
      return hasAccess(this.user.accessLevel, AccessMatrixKeys.Calendar_Events_AddLocation)
  }

  loadEvents() {
    this.$emit("load-events");
  }

  async saveEventImage(e) {
    const path = `${this.currentFacility}/serviceRequests/coverImages/${uuidv4()}`;
    const file: File | Blob | any = await optimizePhotos((e.target as HTMLInputElement).files[0], '');
    const fileSizeInMegabytes = fileSizeToMB(file?.size);

    if (!validFileSize10MB(fileSizeInMegabytes)) {
      return;
    }

    uploadImage({
      path,
      file,
      onStart: () => {
        this.loading = true;
      },
      onComplete: async storage => {
        const uploadedImageUrl = await storage.snapshot.ref.getDownloadURL();
        const resizedImageRef = await firebase.storage().ref().child(`${path}_200x200`);

        try {
          const resizedImageUrl = await waitResizedImage(10, resizedImageRef);
          this.eventData.imageUrl = uploadedImageUrl;
          this.eventData.thumbnailUrl = resizedImageUrl;
        } catch (e) {
          this.eventData.imageUrl = uploadedImageUrl;
          this.eventData.thumbnailUrl = uploadedImageUrl;
        }

        this.loading = false;
      }
    });
  }

  setTime(value: string) {
    const start = value.split("-")[0];
    const end = moment(start, "h:mma")
      .add(30, "m")
      .format("HH:mm");
    const duration = `${start}-${end}`;
    this.eventData.duration = duration;
  }

  saveEventChanges() {

    const formData = { ...this.eventData };
    
    if(this.eventType === EventsTypes.ConciergeRequest){
      formData.isPublished = true
    }

    let eventsData: Calendar[] = [];

    if (!formData.startDate) {
      formData.startDate = moment().format("YYYY-MM-DD");
    }

    if (!formData.endDate) {
      formData.endDate = !formData.endDate
        ? formData.startDate
        : formData.endDate;
    }

    formData.conciergeRequestId = this.request.id;

    formData.going = [this.request.residentId];
    formData.attendeesIds = [this.request.residentId];
    formData.name = formData.name ?? this.request.serviceName;
    formData.description = formData.description ?? "";
    formData.type = this.eventType;
    formData.orgId = this.facility.orgId

    formData.attendees = [
      {
        id: this.resident.id,
        imageUrl: this.resident.imageUrl,
        name: this.resident.name
      }
    ];

    formData.invitees = [this.$route.params.id]

    console.log(formData);
    if (!formData.isRecurring) {
      this.$store
        .dispatch("setupCalendarEvent", {
          newEventData: formData
        })
        .then(event => {
          eventsData.push(event);
          this.saveChangesAndCloseDialog(eventsData);
        });
    } else {
      this.$store
        .dispatch("getRecurringEvents", {
          eventFormData: formData,
          noEndDate: this.noEndDate,
          invitees: [this.$route.params.id],
          monthlyRepeatOn: formData.monthlyRepeatOn
        })
        .then(events => {
          eventsData = events;
          this.saveChangesAndCloseDialog(eventsData);
        });
    }

    this.$refs.form.reset();
  }

  private saveChangesAndCloseDialog(eventsData) {
    this.isEdit ? this.updateEvent(eventsData) : this.createEvent(eventsData);

    this.request = null;
    this.eventData = new CreateEvent();
    this.dialog = false;
  }

  close() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this.$refs.confirmationModal as any)
      .open(
        null,
        `Are you sure you want to cancel the ${this.isEdit ? "edits" : "creation"
        }?`,
        false
      )
      .then(response => {
        if (response && response.agree) {
          this.dialog = false;
        }
      });
  }

  createEvent(eventsData: Calendar[]) {
    if (eventsData && eventsData.length === 1) {
      this.$store.dispatch("createNewCalenderEvent", eventsData[0]);
    } else {
      this.$store.dispatch("createNewCalenderEvents", { events: eventsData });
    }

    this.loadEvents();
    this.dialog = true;
  }

  updateEvent(eventsData: Calendar[]) {
    if (eventsData && eventsData.length === 1) {
      this.$store.dispatch("updateCalenderEvent", eventsData[0]);
    } else {
      this.$store.dispatch("updateCalendarEvents", eventsData);
    }

    this.loadEvents();
    this.dialog = true;
  }

  deleteEvent() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this.$refs.confirmationModal as any)
      .open(null, "Are you sure you want to delete the event?", false)
      .then(response => {
        if (response && response.agree) {
          this.$store.dispatch("deleteCalenderEvent", { id: this.eventData.id, isRecurring: this.eventData?.isRecurring, office365EventId: this.eventData?.office365EventId });
          this.loadEvents();
          this.dialog = false;
        }
      });
  }

  openGalleryPhotos() {
    (this.$refs.GalleryPhotosUploadDlg as any).open();
  }

  uploadGalleryPhoto(image: GalleryImage) {
    this.eventData.imageUrl = image.image;
    this.eventData.thumbnailUrl = image?.thumbnailUrl ?? image.image;
  }

  beforeDestroy() {
    this.request = null;
    this.eventData = new CreateEvent();
  }
}
